import { ReactNode, useMemo } from "react";
// oxlint-disable-next-line no-restricted-imports -- useToaster is the single sanctioned wrapper around react-toastify's `toast`.
import { Id, toast } from "react-toastify/unstyled";
import { TOAST_AUTO_CLOSE_MS } from "@lib/toastConstants";
import { ErrorToast } from "./ErrorToast";

interface AutoCloseOptions {
    /** Keeps the toast on screen instead of auto-dismissing (e.g. while the user still has to act on it). */
    preventAutoClose?: boolean;
    onClose?: () => void;
}

interface ToastCallOptions extends AutoCloseOptions {
    /**
     * Uses react-toastify's own success/warning/error styling instead of the
     * plain default toast. Distinct from `error()`: this skips the app's
     * ErrorToast banner (icon, optional trace ID / Copy ID), so it's for
     * lightweight severity-colored toasts that don't need that chrome.
     */
    variant?: "success" | "warning" | "error";
}

function resolveAutoClose(options?: AutoCloseOptions): number | false {
    return options?.preventAutoClose ? false : TOAST_AUTO_CLOSE_MS;
}

/**
 * Shows the app's red "error banner" toast (icon + optional trace ID / Copy ID
 * affordance). Exported as a plain function - not just the hook's `error` -
 * so non-component code (e.g. the axios response interceptor in
 * `lib/backendClient.ts`) can show it without needing to call a hook. This
 * file stays the single place that imports `toast` from
 * `react-toastify/unstyled`; everywhere else must go through `useToaster()` or
 * this function.
 */
export function showErrorToast(
    content: ReactNode,
    key?: Id,
    options?: AutoCloseOptions & { traceId?: string },
): Id {
    const autoClose = resolveAutoClose(options);
    const render = () => (
        <ErrorToast traceId={options?.traceId}>{content}</ErrorToast>
    );

    if (key && toast.isActive(key)) {
        toast.update(key, { render, autoClose, onClose: options?.onClose });
        return key;
    }

    return toast.error(render, {
        autoClose,
        icon: false,
        toastId: key,
        onClose: options?.onClose,
    });
}

/**
 * Standalone `toast.isActive` check for non-component code (see
 * `showErrorToast` above for why this lives here instead of `useToaster()`).
 */
export function isToastActive(key: Id): boolean {
    return toast.isActive(key);
}

export function dismissAllToasts(): void {
    toast.dismiss();
}

export function useToaster() {
    return useMemo(
        () => ({
            toast: (
                content: ReactNode,
                key?: Id,
                options?: ToastCallOptions,
            ): Id => {
                const autoClose = resolveAutoClose(options);
                const render = () => content;

                if (key && toast.isActive(key)) {
                    toast.update(key, {
                        render: content,
                        autoClose,
                        onClose: options?.onClose,
                    });
                    return key;
                }

                if (options?.variant) {
                    return toast[options.variant](render, {
                        autoClose,
                        toastId: key,
                        onClose: options?.onClose,
                    });
                }

                return toast(render, {
                    autoClose,
                    toastId: key,
                    onClose: options?.onClose,
                });
            },
            error: showErrorToast,
            success: (content: ReactNode, options?: AutoCloseOptions) =>
                toast.success(() => content, {
                    autoClose: resolveAutoClose(options),
                    onClose: options?.onClose,
                }),
            warning: (content: ReactNode, options?: AutoCloseOptions) =>
                toast.warning(() => content, {
                    autoClose: resolveAutoClose(options),
                    onClose: options?.onClose,
                }),
            info: (content: ReactNode) =>
                toast.info(() => content, {
                    autoClose: TOAST_AUTO_CLOSE_MS,
                }),
            dismiss: (key: Id) => toast.dismiss(key),
            update: (
                key: Id,
                update: { render: ReactNode; autoClose?: number | false },
            ) =>
                toast.update(key, {
                    render: update.render,
                    ...(update.autoClose !== undefined && {
                        autoClose: update.autoClose,
                    }),
                }),
            isActive: (key: Id) => toast.isActive(key),
        }),
        [],
    );
}
